import { useState, type ChangeEvent, type FormEvent } from 'react';
import { useToast } from '@/features/ui/toast/useToast';
import { IconX } from './icons';
import type { CrmCustomer } from './customerTypes';

export type OfferFormValues = {
  title: string;
  description: string;
  offer_type: 'nouveau_produit' | 'promotion' | 'remise';
  status: 'envoye' | 'en_attente' | 'accepte' | 'refuse';
  valid_until: string;
};

const empty: OfferFormValues = {
  title: '',
  description: '',
  offer_type: 'nouveau_produit',
  status: 'envoye',
  valid_until: '',
};

type Props = {
  customer: CrmCustomer;
  onClose: () => void;
  onSave: (values: OfferFormValues) => Promise<string | null>;
};

export function OfferForm({ customer, onClose, onSave }: Props) {
  const toast = useToast();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const set =
    (key: keyof OfferFormValues) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm({ ...form, [key]: e.target.value });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    const err = await onSave(form);
    setSaving(false);
    if (err) toast.error(err);
    else onClose();
  }

  return (
    <div className="crm-modal-backdrop" role="presentation" onClick={onClose}>
      <div className="crm-modal crm-modal--sheet" role="dialog" aria-labelledby="crm-offer-form-title" onClick={(e) => e.stopPropagation()}>
        <div className="crm-modal-sticky">
          <div>
            <h2 id="crm-offer-form-title">Nouvelle offre</h2>
            <p className="crm-modal-sub">
              Pour {customer.first_name} {customer.last_name}
            </p>
          </div>
          <button type="button" className="crm-modal-x" onClick={onClose} aria-label="Fermer">
            <IconX />
          </button>
        </div>
        <form onSubmit={(e) => void submit(e)} className="crm-modal-body">
          <div className="field">
            <label>Titre de l&apos;offre *</label>
            <input
              required
              value={form.title}
              onChange={set('title')}
              placeholder="Ex: Promotion été -20%"
            />
          </div>
          <div className="field">
            <label>Description</label>
            <textarea rows={3} value={form.description} onChange={set('description')} />
          </div>
          <div className="grid-2">
            <div className="field">
              <label>Type d&apos;offre</label>
              <select value={form.offer_type} onChange={set('offer_type')}>
                <option value="nouveau_produit">Nouveau produit</option>
                <option value="promotion">Promotion</option>
                <option value="remise">Remise</option>
              </select>
            </div>
            <div className="field">
              <label>Statut</label>
              <select value={form.status} onChange={set('status')}>
                <option value="envoye">Envoyé</option>
                <option value="en_attente">En attente</option>
                <option value="accepte">Accepté</option>
                <option value="refuse">Refusé</option>
              </select>
            </div>
          </div>
          <div className="field">
            <label>Valable jusqu&apos;au</label>
            <input type="date" value={form.valid_until} onChange={set('valid_until')} />
          </div>
          <div className="crm-modal-actions crm-modal-actions--split">
            <button type="button" className="btn crm-btn-outline" onClick={onClose}>
              Annuler
            </button>
            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? 'Enregistrement…' : "Envoyer l'offre"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
